import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { usePageSetting } from "tsi-common-react/src/utils/settings";
import {
    STAR_RATING_CLASS_NAME,
    STAR_RATING_INNER_CONTAINER_CLASS_NAME,
    STAR_RATING_STARS_CLASS_NAME,
    STAR_RATING_STAR_CLASS_NAME,
} from "tsi-common-react/src/utils/starRating";
import { t } from "ttag";

import type { ReviewsBlock as ReviewsBlockValue } from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { ReviewsBlock as ReviewsBlockContainer } from "../../apps/product-detail/ReviewsBlock";

/**
 * Native React port of the legacy server-rendered ``cms/blocks/reviews.html``
 * (#33456). Phase 2 dispatches ``reviews`` through the consolidated page-body
 * component map (``toolboxComponentsMap``), so the WHOLE ``.reviews-block``
 * Django wrapper -- the theme/text-style/background/no-cta/spacing section
 * classes, the ``.l-capped-width`` container, the stars-theme pre-header + star
 * rating link, the title, the inner review carousel and the CTA -- has to live
 * in React (the previous version rendered ONLY the inner carousel, so every
 * consolidated page DROPPED the wrapper -- the wrapper-drop bug).
 *
 * This is a refactor: the markup reproduces ``reviews.html`` exactly so the
 * existing CSS/JS keeps working.
 *
 * RICH TEXT (``value.pre_header`` / ``value.title``): a ``RichTextBlock`` field
 * serializes ALREADY wrapped as ``<div class="rich-text">EXPANDED</div>``
 * (``wagtail.contrib.legacy.richtext`` is installed), which is the SAME HTML the
 * legacy ``{{ value.<field>|richtext }}`` produced. So it is injected RAW via
 * ``dangerouslySetInnerHTML`` inside its legacy container (``.pdp-section__title
 * > .rich-text`` / ``.reviews-block__pre-header > .rich-text``). Do NOT re-wrap
 * with thelabui ``<RichTextBlock>`` (that would double-wrap).
 *
 * The empty guards (``{% if value.title %}`` / ``{% if value.pre_header %}`` /
 * ``{% if not value.title %}reviews-block__spacer--top``) mirror Wagtail
 * ``RichText.__bool__`` == ``bool(source)``: a field is "present" iff its
 * serialized value is non-null and not the cleared wrapper
 * ``<div class="rich-text"></div>`` (the only falsy/cleared form). A
 * ``<p></p>``-only source is Django-truthy and DOES render, so it is NOT empty.
 *
 * STAR RATING: the legacy ``{% include 'tsicommon_cms/blocks/sub-blocks/
 * star-rating.html' with rating=5 class=ratingClasses %}`` is reproduced inline
 * (rather than via the shared ``StarRating`` component, which omits the
 * ``.star-rating__inner-container`` flex wrapper the SCSS centers the row with
 * and uses different SVG markup). ``star-rating.html`` is ENTIRELY gated by
 * ``{% if APP_SLUG != 'tempurca' %}``, so on tempurca the wrapping ``<a>`` still
 * renders but contains no rating -- the gate is reproduced via
 * ``usePageSetting('app_slug')`` (the established tempurca self-gate). Because
 * the legacy hardcoded ``rating=5``, all five stars are full ``star`` sprites.
 */

// ``reviews_url`` / ``brand_color`` are page-derived props (not block fields):
// the toolbox page map's ``reviews`` override threads them from the
// ``reviews_ssr`` side-channel, keyed by block id, on consolidated pages.
type IProps = Immutable<{
    value: ReviewsBlockValue;
    reviews_url: string;
    brand_color: string;
}>;

const STARS_AND_BG = "theme--stars-and-bg";

// The serialized render of a CLEARED richtext field (source ""). Wagtail
// `RichText.__bool__` is `bool(source)`, so the legacy `{% if value.<field> %}`
// guard is falsy ONLY for a cleared field, which serializes to this bare
// wrapper. A `<p></p>`-only source is Django-truthy (the legacy rendered it and
// omitted `reviews-block__spacer--top`), so it must NOT be treated as empty.
const EMPTY_RICHTEXT = '<div class="rich-text"></div>';

// A richtext field is "present" iff it is non-null/non-blank and not the cleared
// wrapper -- matching the legacy `bool(source)` guard.
const richTextPresent = (value: string | null | undefined): boolean =>
    !!value && value !== EMPTY_RICHTEXT;

// The 50x50 `svg-django/star.svg` sprite path the legacy `svg.html` rendered.
const STAR_PATH =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15L.8 21.1c-.4-.4-.8-.9-.8-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5";

// One full star, reproducing the `svg.html` + `svg-django/star.svg` output for
// `src='star' class='star-rating__star' ariaHidden=True title='Star Rating'`.
const StarSVG = (): React.JSX.Element => {
    const title = t`Star Rating`;
    return (
        <svg
            className={STAR_RATING_STAR_CLASS_NAME}
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            xmlSpace="preserve"
            width="50"
            height="50"
            viewBox="0 0 50 50"
        >
            <title>{title}</title>
            <path d={STAR_PATH} />
        </svg>
    );
};

// Inline reproduction of `tsicommon_cms/blocks/sub-blocks/star-rating.html` for
// the legacy hardcoded `rating=5` (five full stars, no `number`). Gated by
// `APP_SLUG != 'tempurca'` exactly like the legacy include.
const ReviewsStarRating = (
    props: Immutable<{ className: string }>,
): React.JSX.Element | null => {
    const appSlug = usePageSetting("app_slug") || "";
    if (appSlug === "tempurca") {
        return null;
    }
    const rating = 5;
    const a11yLabel = interpolate(t`Rated %(rating)s out of 5 stars`, {
        rating: rating,
    });
    return (
        <div
            className={concatClassNames([
                STAR_RATING_CLASS_NAME,
                props.className,
            ])}
        >
            <div className={STAR_RATING_INNER_CONTAINER_CLASS_NAME}>
                <div className={STAR_RATING_STARS_CLASS_NAME}>
                    <span
                        className="ada-screenreader-only"
                        aria-label="star rating"
                    >
                        {a11yLabel}
                    </span>
                    {[0, 1, 2, 3, 4].map((i) => (
                        <StarSVG key={i} />
                    ))}
                </div>
            </div>
        </div>
    );
};

export const ReviewsBlock = (props: IProps): React.JSX.Element => {
    const value = props.value;
    const theme = value.theme?.theme ?? "";
    const isStarsAndBg = theme === STARS_AND_BG;
    const hasTitle = richTextPresent(value.title);
    const hasPreHeader = richTextPresent(value.pre_header);

    // Legacy section class order: `reviews-block`, then (if no title)
    // `reviews-block__spacer--top`, then theme, then text_style, then (only for
    // the stars theme) background_style, then (if no cta) `no-cta`, then the
    // global spacing classes.
    const sectionClassName = concatClassNames([
        "reviews-block",
        !hasTitle ? "reviews-block__spacer--top" : "",
        theme,
        value.text_style ?? "",
        isStarsAndBg ? (value.background_style?.style ?? "") : "",
        !value.cta ? "no-cta" : "",
        getGlobalSpacingClasses(value.spacing),
    ]);

    // Legacy `ratingClasses`: `reviews-block__reviews-rating
    // reviews-block__reviews-rating--{brand_color}`.
    const ratingClasses = `reviews-block__reviews-rating reviews-block__reviews-rating--${props.brand_color}`;

    return (
        <section className={sectionClassName}>
            <div className="l-capped-width">
                {isStarsAndBg ? (
                    <>
                        {hasPreHeader ? (
                            <div
                                className="reviews-block__pre-header"
                                dangerouslySetInnerHTML={{
                                    __html: value.pre_header ?? "",
                                }}
                            />
                        ) : null}
                        <a href={props.reviews_url} title={t`Read Reviews`}>
                            <ReviewsStarRating className={ratingClasses} />
                        </a>
                    </>
                ) : null}

                {hasTitle ? (
                    <div
                        className="pdp-section__title"
                        dangerouslySetInnerHTML={{ __html: value.title ?? "" }}
                    />
                ) : null}

                <ReviewsBlockContainer
                    reviews={value.reviews ?? []}
                    theme={theme}
                />

                {!isStarsAndBg && value.cta ? (
                    <a
                        href={value.cta.url}
                        className="button reviews-block__button"
                    >
                        {value.cta_text}
                    </a>
                ) : null}
            </div>
        </section>
    );
};
